// Phase 8c — rate-card admin schemas (operator-only).
import { z } from 'zod';
import { HstTreatmentSchema, ShiftSettingSchema } from './enums.schema.js';
import { RateIsoDateSchema, RateLeadTimeOverrideSchema, RateMoneyAmountSchema, RateReasonSchema, } from './_shared.schema.js';
// Operator decision 2026-07-28 (dispositions doc, "HST — bill-side treatment"):
// homecare is ALWAYS taxable — HST is charged on every homecare supply
// regardless of the assigned care professional's registrant status. Facilities
// stay unrestricted (EXEMPT / ZERO_RATED / TAXABLE all allowed). This guard
// makes the decision a hard rule at the create boundary so an operator can't
// misconfigure a homecare card as exempt. Supersede has no `setting` field
// (it's fixed by the card being superseded) so it is guarded in the service
// against the existing card's setting instead.
export const HOMECARE_MUST_BE_TAXABLE_MESSAGE = 'Homecare rate cards must be HST TAXABLE — homecare is always taxable regardless of the care professional. Only facility (LTC) rate cards may be exempt.';
export const AdminRateCardCreateRequestSchema = z
    .object({
    roleId: z.string().uuid(),
    setting: ShiftSettingSchema,
    defaultPayRate: RateMoneyAmountSchema,
    defaultBillRate: RateMoneyAmountSchema,
    hstTreatment: HstTreatmentSchema,
    effectiveFrom: RateIsoDateSchema,
    reason: RateReasonSchema,
})
    .superRefine((val, ctx) => {
    if (val.setting === 'HOMECARE' && val.hstTreatment !== 'TAXABLE') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['hstTreatment'],
            message: HOMECARE_MUST_BE_TAXABLE_MESSAGE,
        });
    }
})
    .and(RateLeadTimeOverrideSchema);
export const AdminRateCardSupersedeRequestSchema = z
    .object({
    newDefaultPayRate: RateMoneyAmountSchema,
    newDefaultBillRate: RateMoneyAmountSchema,
    hstTreatment: HstTreatmentSchema,
    effectiveFrom: RateIsoDateSchema,
    reason: RateReasonSchema,
})
    .and(RateLeadTimeOverrideSchema);
export const AdminRateCardDeprecateRequestSchema = z
    .object({
    effectiveTo: RateIsoDateSchema,
    reason: RateReasonSchema,
})
    .and(RateLeadTimeOverrideSchema);
// v0/rate-provenance Phase 4 — in-place edit of an UNUSED rate card (one that
// has priced no shift). VALUE fields only: roleId/setting (identity) + the
// effective dates stay frozen, so an edit can't move the timeline (no overlap/
// gap risk) — rescheduling is delete+create or supersede. No `reason` and no
// lead-time override (lock #4 + no date change ⇒ no lead-time concern). The
// homecare-must-be-taxable rule is enforced service-side against the existing
// row's (frozen) setting. Server-gated to inUse=false; 409 otherwise.
export const AdminRateCardEditRequestSchema = z.object({
    defaultPayRate: RateMoneyAmountSchema,
    defaultBillRate: RateMoneyAmountSchema,
    hstTreatment: HstTreatmentSchema,
});
export const AdminRateCardResponseSchema = z.object({
    id: z.string().uuid(),
    roleId: z.string().uuid(),
    roleCode: z.string(),
    setting: ShiftSettingSchema,
    defaultPayRate: z.string(),
    defaultBillRate: z.string(),
    hstTreatment: HstTreatmentSchema,
    effectiveFrom: RateIsoDateSchema,
    effectiveTo: RateIsoDateSchema.nullable(),
    isCurrent: z.boolean(),
    // v0/rate-provenance — has this row priced any shift? Drives the admin panel's
    // Edit/Delete (unused) vs Supersede/Deprecate (used) action split.
    inUse: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminRateCardListQuerySchema = z.object({
    roleId: z.string().uuid().optional(),
    setting: ShiftSettingSchema.optional(),
    includeHistorical: z.coerce.boolean().default(false),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export const AdminRateCardListResponseSchema = z.object({
    items: z.array(AdminRateCardResponseSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
//# sourceMappingURL=rate-card-admin.schema.js.map